import { useQuery } from '@tanstack/react-query';
import { z } from 'zod';
import client from '../client';
import { parseEnvelope } from '../envelope';
import { canonicalAccountSchema } from './useAccounts';

const uuidSchema = z.string().uuid();
const canonicalMetaSchema = z.union([
  z.object({}).strict(),
  z.object({ alias_of: uuidSchema }).strict(),
]);
const initializationMetaSchema = z.object({ reconciliation_initialization_required: z.literal(true) }).strict();

type CanonicalAccount = z.infer<typeof canonicalAccountSchema>;
export type PrimaryCanonicalAccountDetail = CanonicalAccount & {
  requestedAccountId: string;
  primaryKind: 'canonical' | 'canonical_alias';
  aliasOf?: string;
};
export type PrimaryInitializationAccountDetail = CanonicalAccount & {
  requestedAccountId: string;
  primaryKind: 'initialization_required_physical';
  aliasOf?: never;
};
export type PrimaryAccountDetail = PrimaryCanonicalAccountDetail | PrimaryInitializationAccountDetail;

export function parseCanonicalAccountDetail(payload: unknown, requestedAccountId: string): PrimaryAccountDetail {
  const parsed = parseEnvelope(z.unknown(), payload);
  const data = canonicalAccountSchema.parse(parsed.data);
  if (data.canonical_account_id !== null && data.reconciliation_initialization_required === false) {
    const meta = canonicalMetaSchema.parse(parsed.meta);
    if ('alias_of' in meta && meta.alias_of !== data.canonical_account_id) {
      throw new Error('primary account alias must equal canonical_account_id');
    }
    return {
      ...data,
      requestedAccountId,
      primaryKind: 'alias_of' in meta ? 'canonical_alias' : 'canonical',
      ...('alias_of' in meta ? { aliasOf: meta.alias_of } : {}),
    };
  }
  if (data.canonical_account_id === null && data.reconciliation_initialization_required === true) {
    initializationMetaSchema.parse(parsed.meta);
    return { ...data, requestedAccountId, primaryKind: 'initialization_required_physical' };
  }
  throw new Error('primary account data/meta coherence failed');
}

export function useCanonicalAccount(accountId: string | undefined, enabled: boolean) {
  return useQuery({
    queryKey: ['accounts', 'primary-detail', accountId],
    enabled: Boolean(accountId) && enabled,
    queryFn: async () => {
      const response = await client.get(`/api/accounts/${accountId}`, { params: {} });
      return parseCanonicalAccountDetail(response.data, accountId as string);
    },
  });
}
